// Structure pieces (STR-2/M6.3). PURE (ENG-1).
//
// Authored in the schematic DSL (see tools/schematic.ts) and parsed once here, so
// the generator (main thread, gen worker, server) all build the exact same voxel
// piece from the exact same text — no data round-trip needed. `tools/schematic.ts`
// can also emit these as committed JSON under data/structures/ for the record.
//
// Symbols: '_' carves air (dungeon interiors), '.' keeps the existing block.
// Anchors (@chest / @spawner) tag a cell so the host attaches a block entity
// there (loot chest / mob spawner) after the blocks are stamped.
import { parseSchematic, type Schematic } from '../schematic-place';

// ── Village ──────────────────────────────────────────────────────────────────
const WELL = `
name village/well
palette
# = cobblestone
W = water
L = oak_log[axis=y]
layer
###
#W#
###
layer
L_L
___
L_L
layer
###
###
###
`;

const HOUSE_SMALL = `
name village/house_small
palette
# = cobblestone
P = oak_planks
L = oak_log[axis=y]
D = oak_door[facing=south,open=false]
T = torch
C = chest @chest:chests/village_house
layer
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
layer
L###L
#T_C#
#___#
#___#
L#D#L
layer
L###L
#___#
#___#
#___#
L#D#L
layer
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
`;

const LAMP_POST = `
name village/lamp_post
palette
L = oak_log[axis=y]
T = torch
layer
L
layer
L
layer
L
layer
T
`;

// ── Desert pyramid ───────────────────────────────────────────────────────────
// Layers 0..2 are the buried treasure room (2 loot chests); layers 3.. are the
// stepped sandstone body that pokes above the sand. groundLayer = 3.
const DESERT_PYRAMID = `
name desert_pyramid
palette
# = sandstone
s = sand
C = chest @chest:chests/desert_pyramid
layer
#######
#######
#######
#######
#######
#######
#######
layer
#######
#_____#
#_C_C_#
#_____#
#_____#
#_____#
#######
layer
#######
#######
#######
#######
#######
#######
#######
layer
sssssss
sssssss
sssssss
sssssss
sssssss
sssssss
sssssss
layer
_sssss_
sssssss
sssssss
sssssss
sssssss
sssssss
_sssss_
layer
__sss__
_sssss_
sssssss
sssssss
sssssss
_sssss_
__sss__
layer
___#___
__sss__
_sssss_
_sssss_
_sssss_
__sss__
___#___
`;

// ── Dungeon ──────────────────────────────────────────────────────────────────
// A cobblestone room carved out of stone with a central spawner and two chests.
// Fully buried (ground = bury); '_' carves the interior air.
const DUNGEON = `
name dungeon
palette
# = cobblestone
C = chest @chest:chests/dungeon
S = spawner @spawner:zombie
layer
#######
#######
#######
#######
#######
#######
#######
layer
#######
#C___C#
#_____#
#__S__#
#_____#
#_____#
#######
layer
#######
#_____#
#_____#
#_____#
#_____#
#_____#
#######
layer
#######
#_____#
#_____#
#_____#
#_____#
#_____#
#######
layer
#######
#######
#######
#######
#######
#######
#######
`;

// ── Pillager outpost ─────────────────────────────────────────────────────────
// A dark-oak-and-cobblestone watchtower (MB.6): a razed 5×5 tower with a pillager
// spawner on the first floor and a supply chest on the mid platform. Pillager
// patrols already roam (game/patrol); this is the structure they garrison.
const PILLAGER_OUTPOST = `
name pillager_outpost
palette
# = cobblestone
L = dark_oak_log[axis=y]
P = dark_oak_planks
C = chest @chest:chests/pillager_outpost
S = spawner @spawner:pillager
layer
#####
#####
#####
#####
#####
layer
L###L
#___#
#_S_#
#___#
L###L
layer
L###L
#___#
#___#
#___#
L###L
layer
LPPPL
P___P
P_C_P
P___P
LPPPL
layer
L###L
#___#
#___#
#___#
L###L
layer
LPPPL
P___P
P___P
P___P
LPPPL
layer
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
`;

// Desert well (M-structures): a small sandstone well with a water source, and
// buried suspicious sand at the bottom — a brush target (ties archaeology to
// worldgen). Layer 0 sits one below the surface, layer 1 at ground, layer 2 pokes up.
const DESERT_WELL = `
name desert_well
palette
S = sandstone
W = water
U = suspicious_sand
layer
UUU
UWU
UUU
layer
SSS
SWS
SSS
layer
S_S
___
S_S
`;

// Trail ruins (M-structures): a buried, ruined stone-brick chamber with a
// suspicious-gravel floor (the other brush target) and a relic chest of pottery
// sherds / sniffer eggs — another archaeology tie in real worldgen.
const TRAIL_RUINS = `
name trail_ruins
palette
B = stone_bricks
b = cracked_stone_bricks
M = mossy_stone_bricks
G = suspicious_gravel
g = gravel
C = chest @chest:chests/trail_ruins
layer
BBbBB
BGGGB
bGCGB
BGGGB
BBMBB
layer
BbMBB
b___M
M___b
B___b
BBMbB
layer
BMB_B
_____
M___b
_____
BbB_B
`;

// Buried treasure (M18): a single loot chest buried in the beach sand — the
// heart-of-the-sea prize a treasure map leads to.
const BURIED_TREASURE = `
name buried_treasure
palette
C = chest @chest:chests/buried_treasure
layer
C
`;

// Shipwreck (M18): a beached, half-buried spruce hull with the three classic
// chests — treasure (metals/gems), supply (food/gunpowder), and map (paper +
// compass + a map, the natural in-world source for a treasure map). Layer 0 (the
// keel) buries in the sand; groundLayer 1 sits at the beach surface so the hull
// and its broken deck + mast stump poke out.
const SHIPWRECK = `
name shipwreck
palette
P = spruce_planks
L = spruce_log[axis=y]
A = chest @chest:chests/shipwreck_treasure
B = chest @chest:chests/shipwreck_supply
M = chest @chest:chests/shipwreck_map
layer
.PPP.
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
.PPP.
layer
.PPP.
P_A_P
P___P
P_B_P
P___P
P_M_P
.PPP.
layer
.....
P___P
P___P
P___P
P___P
P___P
.....
layer
.....
.PPP.
.PLP.
.PPP.
.....
.....
.....
`;

// Ocean ruins (M-structures): a broken stone-brick platform sunk on the ocean
// floor with a relic chest and a magma vent. Interiors are '.' (keep), so the
// surrounding water flows straight through the ruin instead of an air bubble —
// this is the first `medium:'water'` structure.
const OCEAN_RUINS = `
name ocean_ruins
palette
B = stone_bricks
b = cracked_stone_bricks
M = mossy_stone_bricks
m = magma_block
s = sand
C = chest @chest:chests/ocean_ruins
layer
sBBbBBs
BMMMMMB
BMbBbMB
bMBCBMb
BMbBbMB
BMMMMMB
sBBbBBs
layer
b.....M
.......
..b.b..
...m...
..b.b..
.......
M.....b
layer
B.....b
.......
.......
.......
.......
.......
b.....M
`;

// Ocean monument (M-structures): the deep-ocean prismarine temple. A 13×13
// prismarine shell around a dark-prismarine core chamber holding an elder-guardian
// spawner and two treasure chests, with guardian spawners in the outer wings and
// sea lanterns lighting the roof. Submerged (`medium:'water'`) and authored with
// '.' interiors so the ocean floods the halls, as a real monument is.
const OCEAN_MONUMENT = `
name ocean_monument
palette
P = prismarine
B = prismarine_bricks
D = dark_prismarine
L = sea_lantern
C = chest @chest:chests/ocean_monument
G = spawner @spawner:guardian
E = spawner @spawner:elder_guardian
layer
PPPPPPPPPPPPP
PBBBBBBBBBBBP
PBPPPPPPPPPBP
PBPBBBBBBBPBP
PBPBDDDDDBPBP
PBPBDDDDDBPBP
PBPBDDDDDBPBP
PBPBDDDDDBPBP
PBPBDDDDDBPBP
PBPBBBBBBBPBP
PBPPPPPPPPPBP
PBBBBBBBBBBBP
PPPPPPPPPPPPP
layer
BBBBBBBBBBBBB
B...........B
B.BBBBBBBBB.B
B.B.......B.B
B.B.PPPPP.B.B
B.B.P.C.P.B.B
B.B.P.E.P.B.B
B.B.P.C.P.B.B
B.B.PPPPP.B.B
B.B.......B.B
B.BBBBBBBBB.B
B...........B
BBBBBBBBBBBBB
layer
B...L...L...B
.............
..B.......B..
...G.....G...
....PPPPP....
....P...P....
....P...P....
....P...P....
....PPPPP....
...G.....G...
..B.......B..
.............
B...L...L...B
layer
B...........B
.............
..B.......B..
.............
....PPPPP....
....P...P....
....P...P....
....P...P....
....PPPPP....
.............
..B.......B..
.............
B...........B
layer
B...........B
.............
..B.......B..
.............
....BBBBB....
....BLLLB....
....BLDLB....
....BLLLB....
....BBBBB....
.............
..B.......B..
.............
B...........B
layer
D...........D
.............
..D.......D..
.............
.............
......D......
.....DLD.....
......D......
.............
.............
..D.......D..
.............
D...........D
`;

// Ancient city (M-structures): the deep-dark ruin, stamped far below the surface
// at a fixed Y in solid deepslate. A deepslate-brick/tile hall with a sculk floor,
// sculk sensors and shriekers wired into the room, a sculk catalyst flanked by two
// loot chests at the centre, and soul campfires for its cold blue light. '_' carves
// the halls out of the surrounding rock (like the dungeon), so it is a real room.
const ANCIENT_CITY = `
name ancient_city
palette
D = deepslate_bricks
T = deepslate_tiles
K = polished_deepslate
k = cobbled_deepslate
s = sculk
S = sculk_sensor
H = sculk_shrieker
f = soul_campfire
c = sculk_catalyst
C = chest @chest:chests/ancient_city
layer
DDDDDDDDDDDDDDD
DTTTTTTTTTTTTTD
DTsssssssssssTD
DTsHsssssssHsTD
DTsssssssssssTD
DTsssKKKKKsssTD
DTsssKCcCKsssTD
DTsssKKKKKsssTD
DTsssKKKKKsssTD
DTsssKKKKKsssTD
DTsssssssssssTD
DTsHsssssssHsTD
DTsssssssssssTD
DTTTTTTTTTTTTTD
DDDDDDDDDDDDDDD
layer
DDDDDDDDDDDDDDD
D_____________D
D_k_f_____f_k_D
D____S___S____D
D_____________D
D____D___D____D
D_____________D
D_____________D
D_____________D
D____D___D____D
D_____________D
D____S___S____D
D_k_f_____f_k_D
D_____________D
DDDDDDDDDDDDDDD
layer
DTTTTTTTTTTTTTD
T_____________T
T_k_________k_T
T_____________T
T_____________T
T____D___D____T
T_____________T
T_____________T
T_____________T
T____D___D____T
T_____________T
T_____________T
T_k_________k_T
T_____________T
DTTTTTTTTTTTTTD
layer
DDDDDDDDDDDDDDD
DTTTTTTTTTTTTTD
DTsTTTTTTTTTsTD
DTTTTTTTTTTTTTD
DTTTTsTTTsTTTTD
DTTTTTTTTTTTTTD
DTTTTTTsTTTTTTD
DTsTTTsHsTTTsTD
DTTTTTTsTTTTTTD
DTTTTTTTTTTTTTD
DTTTTsTTTsTTTTD
DTTTTTTTTTTTTTD
DTsTTTTTTTTTsTD
DTTTTTTTTTTTTTD
DDDDDDDDDDDDDDD
`;

// Nether fortress (M-structures): the classic nether-brick keep. An open bridge
// deck over a walled corridor, a blaze spawner in the guard room, a soul-sand wart
// garden, and a supply chest. Carves its own air with '_' since it is stamped into
// the nether's rock body.
const NETHER_FORTRESS = `
name nether_fortress
palette
B = nether_bricks
S = soul_sand
W = nether_wart
C = chest @chest:chests/nether_fortress
Z = spawner @spawner:blaze
layer
BBBBBBBBBBB
BBBBBBBBBBB
BBBBBBBBBBB
BBBBBBBBBBB
BBBBBBBBBBB
BBBBBBBBBBB
BBBBBBBBBBB
BBBBBBBBBBB
BBBBBBBBBBB
layer
B_________B
B_SSS_____B
B_SSS__Z__B
B_SSS_____B
B_________B
B____C____B
B_________B
B_________B
BBBB___BBBB
layer
B_WWW_____B
B_WWW_____B
B_WWW_____B
B_________B
B_________B
B_________B
B_________B
B_________B
BBBB___BBBB
layer
BBBBBBBBBBB
B_________B
B_________B
B_________B
B_________B
B_________B
B_________B
B_________B
BBBBBBBBBBB
layer
BBBBBBBBBBB
BBBBBBBBBBB
B_________B
B_________B
B_________B
B_________B
B_________B
BBBBBBBBBBB
BBBBBBBBBBB
layer
...........
...........
BBBBBBBBBBB
B_________B
B_________B
B_________B
BBBBBBBBBBB
...........
...........
`;

// Bastion remnant (M-structures): the piglin stronghold. A blackstone shell over a
// polished-blackstone floor with gilded-blackstone veins, a piglin garrison and two
// treasure chests. Like the fortress it carves its own halls out of the nether rock.
const BASTION_REMNANT = `
name bastion_remnant
palette
K = blackstone
P = polished_blackstone
G = gilded_blackstone
A = basalt[axis=y]
C = chest @chest:chests/bastion_remnant
Z = spawner @spawner:piglin
layer
KKKKKKKKKKKKK
KPPPPPPPPPPPK
KPPPGPPPGPPPK
KPPPPPPPPPPPK
KPPPPPPPPPPPK
KPPPPPPPPPPPK
KPPPPGPGPPPPK
KPPPPPPPPPPPK
KPPPPPPPPPPPK
KPPPPPPPPPPPK
KPPPGPPPGPPPK
KPPPPPPPPPPPK
KKKKKKKKKKKKK
layer
KKKKKKKKKKKKK
K___________K
K_A_______A_K
K___________K
K____C_C____K
K___________K
K_____Z_____K
K___________K
K___________K
K_A_______A_K
K___________K
K___________K
KKKKKKKKKKKKK
layer
KKKK_____KKKK
K___________K
K_A_______A_K
K___________K
K___________K
K___________K
K___________K
K___________K
K___________K
K_A_______A_K
K___________K
K___________K
KKKK_____KKKK
layer
KKKK_____KKKK
K___________K
K_A_______A_K
K___________K
K___________K
K___________K
K___________K
K___________K
K___________K
K_A_______A_K
K___________K
K___________K
KKKK_____KKKK
layer
KKKKKKKKKKKKK
KKKKKKKKKKKKK
KK_________KK
KK_________KK
KK_________KK
KK_________KK
KK_________KK
KK_________KK
KK_________KK
KK_________KK
KK_________KK
KKKKKKKKKKKKK
KKKKKKKKKKKKK
`;

// End city (DIM-3): the purpur tower that rises off an outer End island. A
// purpur-block shell on an end-stone-brick footing, purpur-pillar corners, end rods
// for light, a shulker garrison on the upper floor and two treasure chests holding
// the End's rewards (the elytra among them). Carves its own floors with '_'.
const END_CITY = `
name end_city
palette
E = end_stone_bricks
P = purpur_block
I = purpur_pillar[axis=y]
R = end_rod
C = chest @chest:chests/end_city
S = spawner @spawner:shulker
layer
EEEEEEEEE
EEEEEEEEE
EEEEEEEEE
EEEEEEEEE
EEEEEEEEE
EEEEEEEEE
EEEEEEEEE
EEEEEEEEE
EEEEEEEEE
layer
IPPPPPPPI
P_______P
P_______P
P_______P
P___R___P
P_______P
P_______P
P_______P
IPPPPPPPI
layer
IPPPPPPPI
P_______P
P__C_C__P
P_______P
P_______P
P_______P
P_______P
P_______P
IPPPPPPPI
layer
PPPPPPPPP
PPPPPPPPP
PPPPPPPPP
PPPPPPPPP
PPPP_PPPP
PPPPPPPPP
PPPPPPPPP
PPPPPPPPP
PPPPPPPPP
layer
.IPPPPPI.
.P_____P.
.P__S__P.
.P_____P.
.P__R__P.
.P_____P.
.P_____P.
.P_____P.
.IPPPPPI.
layer
.IPPPPPI.
.P_____P.
.P_____P.
.P_____P.
.P_____P.
.P_____P.
.P_____P.
.P_____P.
.IPPPPPI.
layer
.PPPPPPP.
.PPPPPPP.
.PPPPPPP.
.PPPPPPP.
.PPPPPPP.
.PPPPPPP.
.PPPPPPP.
.PPPPPPP.
.PPPPPPP.
layer
..R...R..
.........
.........
.........
.........
.........
.........
.........
..R...R..
`;

// Ocean ruins, WARM variant (M-structures). Minecraft's ruins come in two
// families, and shipping only the cold stone-brick one made every ocean look the
// same. This is the sandstone family: a low sand-drifted ring on a sandy floor,
// authored to the same 7x7 footprint so one structure def can pick either by biome.
const OCEAN_RUINS_WARM = `
name ocean_ruins_warm
palette
S = sandstone
s = sand
m = magma_block
C = chest @chest:chests/ocean_ruins
layer
sSSsSSs
SsssssS
SssSssS
sssCsss
SssSssS
SsssssS
sSSsSSs
layer
S.....s
.......
..S.S..
...m...
..S.S..
.......
s.....S
layer
s.....S
.......
.......
.......
.......
.......
S.....s
`;

// End city ANNEX (DIM-3): the short side wing every real end city has hanging off
// its main tower. A 7x7 purpur box with its own shulker and a single chest — small
// enough to sit beside the tower inside the same region candidate.
const END_CITY_ANNEX = `
name end_city/annex
palette
E = end_stone_bricks
P = purpur_block
I = purpur_pillar[axis=y]
R = end_rod
C = chest @chest:chests/end_city
S = spawner @spawner:shulker
layer
EEEEEEE
EEEEEEE
EEEEEEE
EEEEEEE
EEEEEEE
EEEEEEE
EEEEEEE
layer
IPPPPPI
P_____P
P__C__P
P_____P
P__R__P
P_____P
IPPPPPI
layer
IPPPPPI
P_____P
P__S__P
P_____P
P_____P
P_____P
IPPPPPI
layer
PPPPPPP
PPPPPPP
PPPPPPP
PPPPPPP
PPPPPPP
PPPPPPP
PPPPPPP
layer
.R...R.
.......
.......
.......
.......
.......
.R...R.
`;

// The END SHIP (DIM-3): the floating purpur hull moored beside a city, and the one
// piece worth flying out for — its hold carries the elytra. Authored bow-to-stern
// along +Z with a raised sterncastle, and stamped free of the ground (it FLOATS, so
// its builder gives it its own Y clear of the island rather than a ground layer).
const END_SHIP = `
name end_city/ship
palette
E = end_stone_bricks
P = purpur_block
I = purpur_pillar[axis=y]
R = end_rod
O = obsidian
B = brewing_stand
C = chest @chest:chests/end_ship
S = spawner @spawner:shulker
layer
_PPP_
PPPPP
PPPPP
PPPPP
_PPP_
layer
_P_P_
P___P
P___P
P___P
_PPP_
layer
_P_P_
P_C_P
P_B_P
P___P
_PIP_
layer
_____
_____
_E_E_
_____
__I__
layer
_____
_____
__S__
_____
__R__
layer
_____
_____
_____
_____
__O__
`;

// ── Stronghold (DIM-3) ───────────────────────────────────────────────────────
// The way into The End, and the one structure the world was missing outright: the
// dragon fight, the gateways, the end cities and the elytra all existed, and
// nothing generated a door to any of it.
//
// Four pieces make a real complex rather than a lone room: the portal room, a
// library, a storeroom, and the corridors that join them. Every piece carves its
// own air with '_', so the whole thing is hollowed out of solid stone.

// The portal room. Twelve end-portal frames ring a 3×3 pool over lava, each
// facing INWARD — the eye side looks at the portal, as Minecraft's do — so
// rotating the piece rotates the whole ring coherently through the STR-3 facing
// table. The silverfish spawner is the room's guard.
const STRONGHOLD_PORTAL_ROOM = `
name stronghold/portal_room
palette
K = stone_bricks
M = mossy_stone_bricks
R = cracked_stone_bricks
V = lava
n = end_portal_frame[facing=north,eye=false]
s = end_portal_frame[facing=south,eye=false]
e = end_portal_frame[facing=east,eye=false]
w = end_portal_frame[facing=west,eye=false]
T = torch
Z = spawner @spawner:silverfish
C = chest @chest:chests/stronghold
layer
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKVVVKKKK
KKKKVVVKKKK
KKKKVVVKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
layer
KKKKKKKKKKK
K_Z_____C_K
K_________K
K__KsssK__K
K__e___w__K
K__e___w__K
K__e___w__K
K__KnnnK__K
K_________K
K_________K
KKKKKKKKKKK
layer
KKKKKKKKKKK
K_________K
K_________K
K__T___T__K
K_________K
K_________K
K_________K
K__T___T__K
K_________K
K_________K
KKKKKKKKKKK
layer
KKKKKKKKKKK
M_________K
K_________R
K_________K
K_________M
R_________K
K_________K
M_________R
K_________K
K_________M
KKKKKKKKKKK
layer
KKKKKKKKKKK
K_________M
R_________K
K_________K
M_________R
K_________K
K_________M
K_________K
R_________K
M_________K
KKKKKKKKKKK
layer
KKKKKKKKKKK
KKMKKKRKKMK
KKKKKKKKKKK
KRKKKKKKKRK
KKKKKKKKKKK
KKKMKKKMKKK
KKKKKKKKKKK
KRKKKKKKKRK
KKKKKKKKKKK
KKMKKKRKKMK
KKKKKKKKKKK
`;

// The library: two storeys of bookshelves around an oak mezzanine, with the
// stronghold's best chest on the upper floor.
const STRONGHOLD_LIBRARY = `
name stronghold/library
palette
K = stone_bricks
M = mossy_stone_bricks
B = bookshelf
P = oak_planks
F = oak_fence
T = torch
C = chest @chest:chests/stronghold_library
layer
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
layer
KKKKKKKKKKK
K_BBBBBBB_K
K_________K
KB_______BK
KB___C___BK
KB_______BK
K_________K
K_BBBBBBB_K
KKKKKKKKKKK
layer
KKKKKKKKKKK
K_BBBBBBB_K
K_________K
KB_______BK
KB_______BK
KB_______BK
K_________K
K_BBBBBBB_K
KKKKKKKKKKK
layer
KKKKKKKKKKK
K_PPPPPPP_K
K_PPPPPPP_K
KT_______TK
K_________K
KT_______TK
K_PPPPPPP_K
K_PPPPPPP_K
KKKKKKKKKKK
layer
KKKKKKKKKKK
K_BBBBBBB_K
K_C_____F_K
K_F_____F_K
K_________K
K_F_____F_K
K_F_____F_K
K_BBBBBBB_K
KKKKKKKKKKK
layer
KKKKKKKKKKK
KMKKKKKKKMK
KKKKKKKKKKK
KKKKKKKKKKK
KKKKMKKKKKK
KKKKKKKKKKK
KKKKKKKKKKK
KMKKKKKKKMK
KKKKKKKKKKK
`;

// The storeroom: four chests and a wall shot through with infested stone, which
// is what makes a stronghold worth being careful in.
const STRONGHOLD_STOREROOM = `
name stronghold/storeroom
palette
K = stone_bricks
M = mossy_stone_bricks
R = cracked_stone_bricks
I = infested_stone
T = torch
C = chest @chest:chests/stronghold
layer
KKKKKKKKK
KKKKKKKKK
KKKKKKKKK
KKKKKKKKK
KKKKKKKKK
KKKKKKKKK
KKKKKKKKK
KKKKKKKKK
KKKKKKKKK
layer
KKKKKKKKK
K_C___C_K
K_______K
K_______K
K___T___K
K_______K
K_______K
K_C___C_K
KKKKKKKKK
layer
KKKKKKKKK
K_______K
K_______K
K_______K
K_______K
K_______K
K_______K
K_______K
KKKKKKKKK
layer
KKKIKKIKK
K_______K
K_______K
I_______I
K_______K
I_______I
K_______K
K_______K
KKIKKKIKK
layer
KKKKKKKKK
M_______R
K_______K
K_______M
R_______K
K_______K
M_______R
K_______K
KKKKKKKKK
layer
KKKKKKKKK
KMKKKRKKK
KKKKKKKKK
KKKRKKKMK
KKKKKKKKK
KMKKKKKKK
KKKKKKRKK
KKKKKKKKK
KKKKKKKKK
`;

// A corridor: a 3-wide hall running along X, open at both ends so its end columns
// carve the doorway straight through whichever room wall it meets.
const STRONGHOLD_CORRIDOR = `
name stronghold/corridor
palette
K = stone_bricks
M = mossy_stone_bricks
R = cracked_stone_bricks
B = iron_bars
T = torch
layer
KKKKKKKKKKKKK
KKKKKKKKKKKKK
KKKKKKKKKKKKK
KKKKKKKKKKKKK
KKKKKKKKKKKKK
layer
KKKKKKKKKKKKK
_T_________T_
_____________
_T_________T_
KKKKKKKKKKKKK
layer
KKKKKKKBKKKKK
_____________
_____________
_____________
KKKKKBKKKKKKK
layer
KMKRKKKKKMKRK
_____________
_____________
_____________
KRKKKMKKKRKKK
layer
KKKKKKKKKKKKK
KKMKKKRKKKMKK
KKKKKKKKKKKKK
KKRKKKMKKKRKK
KKKKKKKKKKKKK
`;

// A second end-city tower — slimmer and taller than the main keep — and the BRIDGE
// that reaches it. An end city in Minecraft is a cluster of towers joined by
// walkways; ours was one tower with a wing bolted to its side.
const END_CITY_TOWER = `
name end_city/tower
palette
E = end_stone_bricks
P = purpur_block
I = purpur_pillar[axis=y]
R = end_rod
C = chest @chest:chests/end_city
S = spawner @spawner:shulker
layer
EEEEEEE
EEEEEEE
EEEEEEE
EEEEEEE
EEEEEEE
EEEEEEE
EEEEEEE
layer
IPPPPPI
P_____P
P_____P
P__R__P
P_____P
P_____P
IPPPPPI
layer
IPPPPPI
P_____P
P__C__P
P_____P
P_____P
P_____P
IPPPPPI
layer
IPPPPPI
P_____P
P_____P
P_____P
P_____P
P_____P
IPPPPPI
layer
IPPPPPI
P_____P
P__S__P
P_____P
P_____P
P_____P
IPPPPPI
layer
PPPPPPP
PPPPPPP
PPPPPPP
PPP_PPP
PPPPPPP
PPPPPPP
PPPPPPP
layer
.PPPPP.
P_____P
P_____P
P__C__P
P_____P
P_____P
.PPPPP.
layer
R.....R
.......
.......
.......
.......
.......
R.....R
`;

// The bridge, authored along +Z. Its first and last rows carve a doorway through
// the tower wall they meet and KEEP everything else ('.'), so a walkway opens a
// door rather than punching a five-wide hole in the keep.
const END_CITY_BRIDGE = `
name end_city/bridge
palette
P = purpur_block
I = purpur_pillar[axis=y]
R = end_rod
layer
.....
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
PPPPP
.....
layer
.___.
I___I
P___P
I___I
P___P
I___I
P___P
I___I
P___P
I___I
P___P
I___I
P___P
I___I
.___.
layer
.___.
R___R
_____
_____
_____
R___R
_____
_____
_____
R___R
_____
_____
_____
R___R
.___.
`;

// The bastion's second layout: the HOGLIN STABLE. Same 13×13 footprint as the
// keep — so the region grid, the flatness span and the reach are all shared and
// only the plan changes — but open-roofed pens of crimson stem over a nylium
// floor, with the herd penned in and the treasure out in the open.
const BASTION_STABLES = `
name bastion_stables
palette
K = blackstone
P = polished_blackstone
G = gilded_blackstone
N = crimson_nylium
W = crimson_stem[axis=y]
A = basalt[axis=y]
C = chest @chest:chests/bastion_remnant
H = spawner @spawner:hoglin
Z = spawner @spawner:piglin
layer
KKKKKKKKKKKKK
KNNNNNNNNNNNK
KNNNNNNNNNNNK
KNNNNNNNNNNNK
KNNNNPPPNNNNK
KNNNPPPPPNNNK
KNNNPPGPPNNNK
KNNNPPPPPNNNK
KNNNNPPPNNNNK
KNNNNNNNNNNNK
KNNNNNNNNNNNK
KNNNNNNNNNNNK
KKKKKKKKKKKKK
layer
KKKKKKKKKKKKK
K___________K
K_WWW___WWW_K
K_W_______W_K
K_W_H___H_W_K
K___________K
K_____Z_____K
K___________K
K_W_C___C_W_K
K_W_______W_K
K_WWW___WWW_K
K___________K
KKKKKKKKKKKKK
layer
KKKK_____KKKK
K___________K
K_W_______W_K
K___________K
K_W_______W_K
K___________K
K___________K
K___________K
K_W_______W_K
K___________K
K_W_______W_K
K___________K
KKKK_____KKKK
layer
KKKK_____KKKK
K___________K
K_A_______A_K
K___________K
K___________K
K___________K
K___________K
K___________K
K___________K
K___________K
K_A_______A_K
K___________K
KKKK_____KKKK
layer
KKKKKKKKKKKKK
KK_________KK
K___________K
K___________K
K___________K
K___________K
K___________K
K___________K
K___________K
K___________K
K___________K
KK_________KK
KKKKKKKKKKKKK
`;

/** All parsed pieces, keyed by name. */
export const PIECES: Readonly<Record<string, Schematic>> = Object.freeze({
  'village/well': parseSchematic(WELL),
  'village/house_small': parseSchematic(HOUSE_SMALL),
  'village/lamp_post': parseSchematic(LAMP_POST),
  'desert_pyramid': parseSchematic(DESERT_PYRAMID),
  'dungeon': parseSchematic(DUNGEON),
  'pillager_outpost': parseSchematic(PILLAGER_OUTPOST),
  'desert_well': parseSchematic(DESERT_WELL),
  'trail_ruins': parseSchematic(TRAIL_RUINS),
  'buried_treasure': parseSchematic(BURIED_TREASURE),
  'shipwreck': parseSchematic(SHIPWRECK),
  'ocean_ruins': parseSchematic(OCEAN_RUINS),
  'ocean_ruins_warm': parseSchematic(OCEAN_RUINS_WARM),
  'ocean_monument': parseSchematic(OCEAN_MONUMENT),
  'ancient_city': parseSchematic(ANCIENT_CITY),
  'nether_fortress': parseSchematic(NETHER_FORTRESS),
  'bastion_remnant': parseSchematic(BASTION_REMNANT),
  'end_city': parseSchematic(END_CITY),
  'end_city/annex': parseSchematic(END_CITY_ANNEX),
  'end_city/ship': parseSchematic(END_SHIP),
  'stronghold/portal_room': parseSchematic(STRONGHOLD_PORTAL_ROOM),
  'stronghold/library': parseSchematic(STRONGHOLD_LIBRARY),
  'stronghold/storeroom': parseSchematic(STRONGHOLD_STOREROOM),
  'stronghold/corridor': parseSchematic(STRONGHOLD_CORRIDOR),
  'end_city/tower': parseSchematic(END_CITY_TOWER),
  'end_city/bridge': parseSchematic(END_CITY_BRIDGE),
  'bastion_stables': parseSchematic(BASTION_STABLES),
});

/** Raw DSL sources (for tools/schematic.ts to emit committed JSON). */
export const PIECE_SOURCES: Readonly<Record<string, string>> = Object.freeze({
  'village/well': WELL,
  'village/house_small': HOUSE_SMALL,
  'village/lamp_post': LAMP_POST,
  'desert_pyramid': DESERT_PYRAMID,
  'dungeon': DUNGEON,
  'pillager_outpost': PILLAGER_OUTPOST,
  'desert_well': DESERT_WELL,
  'trail_ruins': TRAIL_RUINS,
  'buried_treasure': BURIED_TREASURE,
  'shipwreck': SHIPWRECK,
  'ocean_ruins': OCEAN_RUINS,
  'ocean_ruins_warm': OCEAN_RUINS_WARM,
  'ocean_monument': OCEAN_MONUMENT,
  'ancient_city': ANCIENT_CITY,
  'nether_fortress': NETHER_FORTRESS,
  'bastion_remnant': BASTION_REMNANT,
  'end_city': END_CITY,
  'end_city/annex': END_CITY_ANNEX,
  'end_city/ship': END_SHIP,
  'stronghold/portal_room': STRONGHOLD_PORTAL_ROOM,
  'stronghold/library': STRONGHOLD_LIBRARY,
  'stronghold/storeroom': STRONGHOLD_STOREROOM,
  'stronghold/corridor': STRONGHOLD_CORRIDOR,
  'end_city/tower': END_CITY_TOWER,
  'end_city/bridge': END_CITY_BRIDGE,
  'bastion_stables': BASTION_STABLES,
});
